import { Button, Typography, type ButtonProps } from '@mui/material';
import type { ExtendButton, ExtendButtonTypeMap } from '@mui/material/Button';
import { type ReactNode } from 'react';

import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

interface ButtonWithIconOwnProps {
  icon?: string | ReactNode;
  iconPosition?: 'left' | 'right';
  text: ReactNode;
  isAuthorized?: boolean;
  dataTestId?: string;
  textClassName?: string;
  disabledTooltip?: string;
}

type ButtonWithIconTypeMap = ExtendButtonTypeMap<{
  props: ButtonWithIconOwnProps;
  defaultComponent: 'button';
}>;

type ButtonWithIconProps = ButtonWithIconOwnProps &
  Omit<
    ButtonProps,
    keyof ButtonWithIconOwnProps | 'children' | 'startIcon' | 'endIcon'
  >;

export const ButtonWithIcon = (({
  icon,
  text,
  dataTestId,
  className,
  textClassName,
  iconPosition = 'left',
  isAuthorized = true,
  disabledTooltip,
  disabled,
  ...rest
}: ButtonWithIconProps) => {
  const iconElement: ReactNode =
    typeof icon === 'string' ? <i className={`${icon} m-[2px]`} /> : icon;

  return (
    <AuthorizationTooltip
      isAuthorized={isAuthorized}
      title={disabled ? disabledTooltip : undefined}
    >
      <Button
        size='small'
        className={className}
        disabled={!isAuthorized || disabled}
        data-testid={dataTestId}
        {...rest}
        startIcon={iconPosition === 'left' ? iconElement : undefined}
        endIcon={iconPosition === 'right' ? iconElement : undefined}
      >
        <Typography
          noWrap
          variant='button'
          className={textClassName}
          sx={{ color: 'currentcolor' }}
        >
          {text}
        </Typography>
      </Button>
    </AuthorizationTooltip>
  );
  // Cast required: a plain function can't structurally satisfy ExtendButton's two overloads (button vs anchor ref types differ).
}) as unknown as ExtendButton<ButtonWithIconTypeMap>;
